import { TileSize } from "game-common/models"
import { Circle, Container, Graphics, Text } from "pixi.js"
import { BuildingPrintGroup } from "../../../../client_models"
import { SCALER } from "../../../../client_util"

interface CountdownRow {
    root: Container
    wheel: Graphics
    time: Text
    group: BuildingPrintGroup
}

// These dials are children of the world surface. Their tile coordinates never depend on
// the player, mouse, or camera; they travel through the same transforms as the walls.
export class BuildingPrintCountdown extends Container {
    private rows = new Map<number, CountdownRow>()
    private hovered = new Set<number>()

    constructor(private worldSurface: () => Container) {
        super()
        this.zIndex = 100001 // Above pending wall ghosts.
        this.visible = false
    }

    isPointerOver = () => this.visible && this.hovered.size > 0

    setGroups = (groups: BuildingPrintGroup[]) => {
        const ids = new Set(groups.map(group => group.id))
        this.rows.forEach((row, id) => {
            if (ids.has(id)) return
            this.removeChild(row.root)
            row.root.destroy({ children: true })
            this.rows.delete(id)
            this.hovered.delete(id)
        })
        groups.forEach(group => {
            let row = this.rows.get(group.id)
            if (!row) {
                const root = new Container()
                // Correct the stage's vertical stretch while preserving the tile anchor.
                root.scale.set(0.5, 0.5 / SCALER)
                root.interactive = true
                root.cursor = "pointer"
                root.hitArea = new Circle(0, 0, 14)
                root.on("pointerover", () => this.hovered.add(group.id))
                root.on("pointerout", () => this.hovered.delete(group.id))
                root.on("pointerdown", event => {
                    event.stopPropagation()
                    this.rows.get(group.id)?.group.onCancel()
                })
                const wheel = new Graphics()
                root.addChild(wheel)
                const time = new Text("", {
                    fontFamily: "Arial", fontSize: 6.5, fill: 0xffedc7,
                    fontWeight: "bold", stroke: 0x171c23, strokeThickness: 2,
                })
                time.anchor.set(0.5)
                time.position.set(0, 19)
                root.addChild(time)
                row = { root, wheel, time, group }
                this.rows.set(group.id, row)
                this.addChild(root)
            }
            row.group = group
            // Set once from the group's final tile, in world pixels.
            row.root.position.set((group.anchor.x + 0.5) * TileSize, (group.anchor.y + 0.5) * TileSize)
        })
        this.visible = groups.length > 0
        this.update()
    }

    update = () => {
        if (!this.visible) return
        const surface = this.worldSurface()
        if (this.parent !== surface) surface.addChild(this)
        const now = Date.now()
        this.rows.forEach(({ wheel, time, group }) => {
            const remaining = Math.max(0, group.duration - (now - group.startedAt))
            const fraction = Math.min(1, remaining / group.duration)
            const hover = this.hovered.has(group.id)
            wheel.clear()
            wheel.beginFill(0x000000, 0.22).drawCircle(0, 1.5, 14).endFill()
            wheel.lineStyle(0.7, hover ? 0xe69b80 : 0x8e7b58)
                .beginFill(hover ? 0x3c2928 : 0x20262d, 0.97).drawCircle(0, 0, 13).endFill()
            wheel.lineStyle(2, 0x454b54).drawCircle(0, 0, 10.5)
            if (fraction > 0) {
                const angle = -Math.PI / 2 + fraction * Math.PI * 2
                wheel.lineStyle(2, hover ? 0xffad89 : 0xffd166)
                    .moveTo(0, -10.5).arc(0, 0, 10.5, -Math.PI / 2, angle)
                // The moving endpoint makes the shrinking countdown arc easy to read.
                wheel.lineStyle(0).beginFill(0xffedc7)
                    .drawCircle(Math.cos(angle) * 10.5, Math.sin(angle) * 10.5, 1.6).endFill()
            }
            // A clock's twelve-o'clock notch reinforces that the shrinking ring is a timer.
            wheel.lineStyle(1, 0xffedc7, 0.8).moveTo(0, -12).lineTo(0, -10)
            wheel.lineStyle(1.5, hover ? 0xffb59e : 0xffedc7)
                .moveTo(-3, -3).lineTo(3, 3).moveTo(3, -3).lineTo(-3, 3)
            time.text = `${(remaining / 1000).toFixed(1)}s`
        })
    }
}
